import { isNil } from "client/Misc/utils";
import { isCancelledError } from "./cancellation";
import { HttpRequestError } from "client/Api/httpClient";

interface Options {
	intervalInSeconds: number;
	shouldRemoveMinimalIntervalConstraint?: boolean;
	shouldInvokeImmediately?: boolean;
	callback: (signal: AbortSignal) => Promise<unknown>;
}

class Poller {
	private readonly _options: Options;
	private _intervalHandle: SetTimeoutHandle | null;
	private _cancellation: AbortController | undefined;

	public get isActive(): boolean {
		return Boolean(this._intervalHandle);
	}

	constructor(options: Options) {
		this._options = options;
		this._intervalHandle = null;
	}

	public start() {
		const MINIMAL_INTERVAL_IN_SECONDS: number = 60;

		let { intervalInSeconds: interval } = this._options;
		const {
			callback,
			shouldInvokeImmediately = true,
			shouldRemoveMinimalIntervalConstraint
		} = this._options;
		// Prevent accidental DDOS-ing
		if ((isNil(interval) || interval < MINIMAL_INTERVAL_IN_SECONDS) && !shouldRemoveMinimalIntervalConstraint) {
			console.warn(`The updateInterval is smaller then ${MINIMAL_INTERVAL_IN_SECONDS} seconds`);
			interval = MINIMAL_INTERVAL_IN_SECONDS;
		}

		this.startPollingWithAutomaticStopOnForbidden(
			interval * 1000,
			callback,
			shouldInvokeImmediately
		);
	}

	private startPollingWithAutomaticStopOnForbidden(
		intervalInMs: number,
		callbackProducingPromise: (signal: AbortSignal) => Promise<unknown>,
		shouldInvokeImmediately: boolean
	) {
		if (this.isActive) {
			throw new Error("Poller is already active");
		}

		let isWaitingForResponse: boolean = false;

		const callbackWrapper = async () => {

			if (isWaitingForResponse) {
				console.warn("Skipping polling interval because we are still waiting for a previous response");
				return;
			}

			this._cancellation = new AbortController();
			isWaitingForResponse = true;
			try {
				await callbackProducingPromise(this._cancellation.signal);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				const isForbiddenError: boolean = e instanceof(HttpRequestError) && e.statusCode === 403;

				if (isForbiddenError) {
					this.stop();
				}
			} finally {
				isWaitingForResponse = false;
				this._cancellation = undefined;
			}
		};

		this._intervalHandle = setInterval(callbackWrapper, intervalInMs);

		if (shouldInvokeImmediately) {
			// Invoke directly
			callbackWrapper();
		}
	}

	public stop() {
		if (this._intervalHandle) {
			clearInterval(this._intervalHandle);
			this._intervalHandle = null;
		}

		if (this._cancellation) {
			this._cancellation.abort();
		}
	}

	public destroy() {
		this.stop();
	}
}

export {
	Poller
};